import { classes } from "common/theme/theme";
import { IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import React, { FC } from "react";
import RemoveIcon from "@mui/icons-material/Remove";

interface PropertySelectorProps {
  Icon: React.FC<{ width: string; height: string; className: string }>;
  iconTooltip: string;
  propertyExamples: Map<string, Set<string>>;
  selectedPropertyList: string[];
  updateSelectedPropertyList: (propertyList: string[]) => void;
}

export const PropertySelector: FC<PropertySelectorProps> = ({
  Icon,
  iconTooltip,
  propertyExamples,
  selectedPropertyList,
  updateSelectedPropertyList,
}) => {
  const { t } = useTranslation();

  const workingPropertyExamples = new Map(propertyExamples);
  const reorderedPropertyList: {
    propName: string;
    examples: Set<string>;
    selected: boolean;
  }[] = [];

  for (const propName of selectedPropertyList) {
    const examples = workingPropertyExamples.get(propName) ?? new Set<string>();
    workingPropertyExamples.delete(propName);

    reorderedPropertyList.push({
      propName,
      examples,
      selected: true,
    });
  }

  for (const [propName, examples] of workingPropertyExamples.entries()) {
    reorderedPropertyList.push({
      propName,
      examples,
      selected: false,
    });
  }

  return (
    <div className="grid grid-cols-[4rem_1fr_2fr] grid-rows-[min-content] flex-auto flex-grow-0 min-h-0 border-gray-400 border-[1px] border-solid">
      <div className="grid col-span-full grid-cols-subgrid justify-items-center items-center p-2 bg-lighten-300 border-gray-400 border-0 border-b-[1px] border-solid">
        <span title={iconTooltip}>
          <Icon width="24" height="24" className="align-middle" />
        </span>
        <div>{t("views.farms.importFarms.property")}</div>
        <div>{t("views.farms.importFarms.examples")}</div>
      </div>
      <div className="grid col-span-full grid-cols-subgrid overflow-y-auto">
        {reorderedPropertyList.map(({ propName, examples, selected }) => (
          <div
            className={classes(
              "grid col-span-full grid-cols-subgrid items-center p-2 border-gray-400 border-0 border-b-[1px] last:border-b-0 border-solid",
              { "bg-primary-400": selected }
            )}
            key={propName}
          >
            <div className="flex flex-col items-center">
              {selected ? (
                <IconButton
                  onClick={() => {
                    const newList = [...selectedPropertyList];
                    const oldIndex = newList.indexOf(propName);
                    newList.splice(oldIndex, 1);
                    updateSelectedPropertyList(newList);
                  }}
                >
                  <RemoveIcon className="text-white" />
                </IconButton>
              ) : (
                <IconButton
                  onClick={() => {
                    const newList = [...selectedPropertyList];
                    newList.push(propName);
                    updateSelectedPropertyList(newList);
                  }}
                >
                  <AddIcon className="text-white" />
                </IconButton>
              )}
            </div>
            <div>
              <code>{propName}</code>
            </div>
            <div>
              {[...examples.values()]
                .slice(0, Math.min(examples.size, 7))
                .map((ex) => (
                  <>
                    {ex}
                    <br />
                  </>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
